import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  X, 
  Plus, 
  AlertCircle, 
  CheckCircle, 
  Globe, 
  FileText, 
  Calendar,
  MapPin,
  User,
  Shield,
  Hash
} from 'lucide-react';

const CreateNoticeModal = ({ isOpen, onClose, onNoticeCreated, noticeStats, embedded = false }) => {
  const [formData, setFormData] = useState({
    title: '',
    notice_type: '',
    body_text: '',
    pdf_upload: null,
    show_full_name: true,
    location: ''
  });
  const [uploadMode, setUploadMode] = useState('text'); // 'text' or 'pdf'
  const [noticeTypes, setNoticeTypes] = useState([
    'Notice of Conditional Acceptance (Respondent has X days to rebut under penalty of default)',
    'Notice of Default & Opportunity to Cure',
    'Notice of Dishonor (Non-Response / Incomplete / Refused for Cause)',
    'Notice of Administrative Fault',
    'Public Notice of Mailing (proof of mailing to a respondent or court agency)',
    'Affidavit of Non-Response / Acquiescence',
    'Notice of Breach of Contract / Public Duty',
    'Other'
  ]); // Temporarily hardcoded for testing
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Fetch configuration data
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        console.log('Fetching notice types from:', `${process.env.REACT_APP_BACKEND_URL}/api/notices/config/notice-types`);
        const typesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/config/notice-types`);

        if (typesResponse.ok) {
          const types = await typesResponse.json();
          console.log('Fetched notice types:', types);
          setNoticeTypes(types);
        } else {
          console.error('Failed to fetch notice types:', typesResponse.status, typesResponse.statusText);
        }
      } catch (error) {
        console.error('Error fetching config:', error);
      }
    };

    console.log('useEffect triggered - isOpen:', isOpen, 'embedded:', embedded);
    if (isOpen || embedded) {
      console.log('Calling fetchConfig...');
      fetchConfig();
    }
  }, [isOpen, embedded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare form data - ensure either text or PDF is provided
      const submitData = { ...formData };
      if (uploadMode === 'pdf' && !submitData.pdf_upload) {
        setError('Please upload a PDF file or switch to text mode');
        setLoading(false);
        return;
      }
      if (uploadMode === 'text' && !submitData.body_text) {
        setError('Please enter notice content or switch to PDF mode');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrl = `${backendUrl}/api/notices/create`;
      
      console.log('Creating notice with token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
      console.log('Backend URL:', backendUrl);
      console.log('Full URL:', fullUrl);
      console.log('Submit data:', submitData);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      console.log('Create notice response status:', response.status);
      const data = await response.json();
      console.log('Create notice response data:', data);

      if (response.ok) {
        console.log('Notice created successfully!');
        setSuccess(true);
        setTimeout(() => {
          onNoticeCreated();
          if (!embedded) {
            onClose();
          }
          resetForm();
        }, 2000);
      } else {
        console.error('Failed to create notice:', response.status, data);
        setError(data.detail || `Failed to create notice (${response.status})`);
      }
    } catch (error) {
      console.error('Catch block error:', error);
      setError(`Error: ${error.message || 'Network error. Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      notice_type: '',
      body_text: '',
      pdf_upload: null,
      show_full_name: true,
      location: ''
    });
    setUploadMode('text');
    setError('');
    setSuccess(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          pdf_upload: e.target.result // Base64 encoded PDF
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setError('Please select a valid PDF file');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const canSubmit = formData.title && formData.notice_type && 
    ((uploadMode === 'text' && formData.body_text) || (uploadMode === 'pdf' && formData.pdf_upload));
  const isLimitReached = noticeStats && noticeStats.remaining_posts === 0 && noticeStats.plan_limit !== -1;

  const modalContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
            <Plus className="w-6 h-6 text-blue-600 mr-2" />
            Create Public Notice
          </h2>
          <p className="text-gray-600 mt-1">Publish your lawful declaration to the world</p>
        </div>
        {!embedded && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Stats Display */}
      {noticeStats && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{noticeStats.monthly_notices}</div>
                <div className="text-xs text-blue-500">Used This Month</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">
                  {noticeStats.plan_limit === -1 ? '∞' : noticeStats.remaining_posts}
                </div>
                <div className="text-xs text-green-500">Remaining</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-gray-600">
                  {noticeStats.plan_limit === -1 ? 'Unlimited' : noticeStats.plan_limit}
                </div>
                <div className="text-xs text-gray-500">Monthly Limit</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Limit Warning */}
      {isLimitReached && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-yellow-800">Monthly Limit Reached</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You've used all {noticeStats.plan_limit} notices for this month. 
                Upgrade your plan for more posting capacity.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="text-sm font-semibold text-green-800">Notice Published Successfully!</h3>
              <p className="text-sm text-green-700 mt-1">
                Your public notice is now live and timestamped with sovereign proof.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notice Title *
          </label>
          <Input
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="e.g., Affidavit of Status – John Doe"
            maxLength={200}
            disabled={loading || isLimitReached}
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">{formData.title.length}/200 characters</p>
        </div>

        {/* Notice Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notice Type *
          </label>
          <select
            value={formData.notice_type}
            onChange={(e) => handleInputChange('notice_type', e.target.value)}
            disabled={loading || isLimitReached}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select notice type...</option>
            {noticeTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location (Optional)
          </label>
          <Input
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            placeholder="e.g., Houston, Texas"
            maxLength={200}
            disabled={loading || isLimitReached}
            className="w-full"
          />
        </div>

        {/* Content Input Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Notice Content *
          </label>
          
          {/* Mode Toggle */}
          <div className="flex space-x-4 mb-4">
            <button
              type="button"
              onClick={() => setUploadMode('text')}
              disabled={loading || isLimitReached}
              className={`px-4 py-2 rounded-md border font-medium text-sm transition-colors ${
                uploadMode === 'text'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Manual Text Entry
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('pdf')}
              disabled={loading || isLimitReached}
              className={`px-4 py-2 rounded-md border font-medium text-sm transition-colors ${
                uploadMode === 'pdf'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Upload PDF Document
            </button>
          </div>

          {/* Text Input Mode */}
          {uploadMode === 'text' && (
            <div>
              <Textarea
                value={formData.body_text}
                onChange={(e) => handleInputChange('body_text', e.target.value)}
                placeholder="Enter your lawful declaration, affidavit, or notice content here..."
                rows={8}
                disabled={loading || isLimitReached}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: You can paste content from the Remedy Generator templates
              </p>
            </div>
          )}

          {/* PDF Upload Mode */}
          {uploadMode === 'pdf' && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                disabled={loading || isLimitReached}
                className="hidden"
                id="pdf-upload"
              />
              <label
                htmlFor="pdf-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <Plus className="w-12 h-12 text-gray-400 mb-3" />
                <span className="text-sm font-medium text-gray-700 mb-1">
                  Click to upload PDF document
                </span>
                <span className="text-xs text-gray-500">
                  PDF files only, max 10MB
                </span>
              </label>
              {formData.pdf_upload && (
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                  <CheckCircle className="w-4 h-4 text-green-600 inline mr-2" />
                  <span className="text-sm text-green-700">PDF uploaded successfully</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Privacy Setting */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.show_full_name}
              onChange={(e) => handleInputChange('show_full_name', e.target.checked)}
              disabled={loading || isLimitReached}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Show Full Name</span>
              <p className="text-xs text-gray-500">
                Display your full name publicly with this notice. Uncheck to publish anonymously.
              </p>
            </div>
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-gray-500">
            <Hash className="w-4 h-4 inline mr-1" />
            Each notice gets timestamped with sovereign proof
          </div>
          
          <div className="flex space-x-3">
            {!embedded && (
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
            )}
            <Button 
              type="submit" 
              disabled={!canSubmit || loading || isLimitReached}
              className="flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Publishing...</span>
                </>
              ) : (
                <>
                  <Globe className="w-4 h-4" />
                  <span>Publish to the World</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );

  if (embedded) {
    return <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="p-6">
        {modalContent}
      </CardContent>
    </Card>;
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {modalContent}
        </div>
      </div>
    </div>
  );
};

export default CreateNoticeModal;